/**
 * 양식 사용 권한(부서·담당) CRUD (환경설정)
 * SSOT: D:\295\erp\CM\design\05_양식레지스트리_매칭.md · P2
 * — ACL 미등록 = 전원 허용. 등록 시 부서 OR 로그인ID 일치.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import useTenantCode from 'hooks/useTenantCode';
import { useAuth } from 'contexts/AuthContext';
import { Card } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, ActionButtonGroup } from 'components/forms';
import { showAlert } from 'utils/toastBridge';
import { DOC_TYPE, DOC_TYPE_LABEL, DOC_TYPE_OPTIONS } from 'system/formDocTypes';
import {
  hydrateFormRegistry,
  ensureFormRegistry,
  listForms,
  listFormAcls,
  upsertFormAcl,
  deleteFormAcl,
  canUseForm,
  getForm,
} from 'system/formRegistryStore';
import { loadUsers } from 'master/userAccountStore';
import { loadDepts } from 'master/deptStore';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  padding: 10px 12px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th,
  td {
    border: 1px solid var(--erp-border);
    padding: 4px 8px;
    text-align: left;
  }
  th {
    background: var(--erp-surface-muted);
    font-weight: 600;
  }
  tr.active td {
    background: var(--erp-color-bg-selected, var(--erp-surface-muted));
  }
  tr.disabled td {
    color: var(--erp-text-secondary);
    opacity: 0.75;
  }
`;

const Meta = styled.p`
  margin: 0;
  font-size: 11px;
  color: var(--erp-text-secondary);
`;

const HintList = styled.ul`
  margin: 4px 0 0;
  padding-left: 18px;
  font-size: 11px;
  color: var(--erp-text-secondary);
`;

const emptyDraft = (tenantCode, formId) => ({
  aclId: '',
  tenantCode,
  formId: formId || '',
  allowDeptCodes: '',
  allowUserIds: '',
  remark: '',
  useYn: 'Y',
});

export default function FormAclPanel() {
  const tenantCode = useTenantCode();
  const { user } = useAuth();
  const [acls, setAcls] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState(DOC_TYPE.EXP_RESOLUTION);
  const [deptHint, setDeptHint] = useState('');
  const [userHint, setUserHint] = useState('');

  const methods = useForm({
    defaultValues: {
      ...emptyDraft(tenantCode, ''),
      filterDocType: DOC_TYPE.EXP_RESOLUTION,
      testDept: '',
      testUserId: '',
    },
  });
  const { reset, getValues, setValue } = methods;

  const refresh = useCallback(async () => {
    await hydrateFormRegistry();
    const list = listFormAcls({ tenantCode, includeDisabled: true });
    setAcls(list);
    if (selectedId && !list.some((a) => a.aclId === selectedId)) setSelectedId('');
  }, [tenantCode, selectedId]);

  useEffect(() => {
    refresh();
  }, [tenantCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [depts, users] = await Promise.all([
          loadDepts(tenantCode),
          loadUsers(tenantCode, user || {}),
        ]);
        if (cancelled) return;
        const d = (depts || [])
          .slice(0, 8)
          .map((x) => x.deptCode)
          .filter(Boolean)
          .join(', ');
        const u = (users || [])
          .slice(0, 8)
          .map((x) => x.loginId || x.userId)
          .filter(Boolean)
          .join(', ');
        setDeptHint(d ? `예: ${d}` : '');
        setUserHint(u ? `예: ${u}` : '');
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCode, user]);

  const formOptions = useMemo(() => {
    const dt = getValues('filterDocType') || docTypeFilter;
    return listForms({ docType: dt }).map((f) => ({
      value: f.formId,
      label: `${f.formName || f.formId} (${f.formId})`,
    }));
  }, [docTypeFilter, acls, getValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAcl = useCallback(
    (row) => {
      if (!row) return;
      setSelectedId(row.aclId);
      const form = getForm(row.formId);
      if (form?.docType) {
        setDocTypeFilter(form.docType);
        setValue('filterDocType', form.docType);
      }
      reset({
        ...getValues(),
        aclId: row.aclId,
        tenantCode: row.tenantCode,
        formId: row.formId,
        allowDeptCodes: (row.allowDeptCodes || []).join(', '),
        allowUserIds: (row.allowUserIds || []).join(', '),
        remark: row.remark || '',
        useYn: row.useYn || 'Y',
      });
    },
    [getValues, reset, setValue],
  );

  const handleSearch = useCallback(() => {
    refresh();
    showAlert(`사용 권한 ${listFormAcls({ tenantCode, includeDisabled: true }).length}건`, 'info');
  }, [refresh, tenantCode]);

  const handleAdd = useCallback(() => {
    const dt = getValues('filterDocType') || DOC_TYPE.EXP_RESOLUTION;
    const forms = listForms({ docType: dt, useYn: 'Y' });
    const draft = emptyDraft(tenantCode, forms[0]?.formId || '');
    setSelectedId('');
    reset({ ...getValues(), ...draft });
    showAlert('신규 ACL · 부서·담당을 비우면 해당 행은 전원 허용입니다.', 'info');
  }, [getValues, reset, tenantCode]);

  const handleSave = useCallback(() => {
    const v = getValues();
    try {
      const saved = upsertFormAcl({
        aclId: v.aclId || undefined,
        tenantCode: v.tenantCode || tenantCode,
        formId: v.formId,
        allowDeptCodes: v.allowDeptCodes,
        allowUserIds: v.allowUserIds,
        remark: v.remark,
        useYn: v.useYn,
      });
      setSelectedId(saved.aclId);
      setValue('aclId', saved.aclId);
      refresh();
      showAlert(`ACL 저장: ${saved.aclId}`, 'success');
    } catch (e) {
      showAlert(e?.message || '저장 실패', 'error');
    }
  }, [getValues, tenantCode, setValue, refresh]);

  const handleDelete = useCallback(() => {
    const id = getValues('aclId') || selectedId;
    if (!id) {
      showAlert('삭제할 ACL을 선택하세요.', 'warning');
      return;
    }
    deleteFormAcl(id);
    setSelectedId('');
    reset({ ...getValues(), ...emptyDraft(tenantCode, '') });
    refresh();
    showAlert(`ACL 비활성: ${id}`, 'success');
  }, [getValues, selectedId, reset, tenantCode, refresh]);

  const handleTest = useCallback(() => {
    const v = getValues();
    const form = getForm(v.formId);
    if (!form) {
      showAlert('양식을 선택하세요.', 'warning');
      return;
    }
    const ok = canUseForm(form, {
      tenantCode,
      deptCode: v.testDept,
      userId: v.testUserId || user?.id || '',
    });
    showAlert(
      ok
        ? `허용: ${form.formId} · 부서=${v.testDept || '—'} · 사용자=${v.testUserId || user?.id || '—'}`
        : `거부: ${form.formId} · ACL에 부서·담당이 맞지 않습니다`,
      ok ? 'success' : 'warning',
    );
  }, [getValues, tenantCode, user]);

  const filterFields = useMemo(
    () => [
      {
        name: 'filterDocType',
        label: '업무(양식목록)',
        type: 'select',
        options: DOC_TYPE_OPTIONS,
        labelWidth: SEARCH_LABEL_W,
      },
    ],
    [],
  );

  const editFields = useMemo(
    () => [
      { name: 'aclId', label: 'ACL ID', type: 'text', readonly: true, labelWidth: SEARCH_LABEL_W },
      { name: 'tenantCode', label: '기관', type: 'text', labelWidth: SEARCH_LABEL_W },
      {
        name: 'formId',
        label: '양식',
        type: 'select',
        options: formOptions.length ? formOptions : [{ value: '', label: '(양식 없음)' }],
        labelWidth: SEARCH_LABEL_W,
      },
      {
        name: 'allowDeptCodes',
        label: '허용 부서',
        type: 'text',
        labelWidth: SEARCH_LABEL_W,
        placeholder: '쉼표 구분 · 비우면 부서 제한 없음',
      },
      {
        name: 'allowUserIds',
        label: '허용 담당',
        type: 'text',
        labelWidth: SEARCH_LABEL_W,
        placeholder: '로그인ID 쉼표 구분',
      },
      { name: 'remark', label: '비고', type: 'text', labelWidth: SEARCH_LABEL_W },
      {
        name: 'useYn',
        label: '사용',
        type: 'select',
        options: [
          { value: 'Y', label: 'Y' },
          { value: 'N', label: 'N' },
        ],
        labelWidth: SEARCH_LABEL_W,
      },
    ],
    [formOptions],
  );

  const testFields = useMemo(
    () => [
      { name: 'testDept', label: '시험 부서', type: 'text', labelWidth: SEARCH_LABEL_W },
      { name: 'testUserId', label: '시험 담당', type: 'text', labelWidth: SEARCH_LABEL_W },
    ],
    [],
  );

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', onClick: handleSearch, shortcut: 'F7' },
      { id: 'add', label: '추가', onClick: handleAdd, shortcut: 'F8' },
      { id: 'delete', label: '삭제', onClick: handleDelete, shortcut: 'F9', variant: 'danger' },
      { id: 'save', label: '저장', onClick: handleSave, shortcut: 'F10', variant: 'primary' },
      { id: 'test', label: '권한 시험', onClick: handleTest },
    ],
    [handleSearch, handleAdd, handleDelete, handleSave, handleTest],
  );

  return (
    <Stack>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <Panel>
          <strong style={{ fontSize: 13 }}>조회</strong>
          <FormArea
            dense
            fields={filterFields}
            onFormChange={(formData, _prev, changed) => {
              if (changed === 'filterDocType' && formData?.filterDocType) {
                setDocTypeFilter(formData.filterDocType);
              }
            }}
          />
          <Meta>
            기관 {tenantCode} · 업무 필터 {DOC_TYPE_LABEL[docTypeFilter] || docTypeFilter} (양식
            선택용) · ACL은 기관 전 양식 목록
          </Meta>
          <HintList>
            <li>해당 양식에 ACL이 없으면 전원 사용 가능</li>
            <li>ACL이 있으면 허용 부서 OR 허용 담당(로그인ID) 중 하나 일치</li>
            {deptHint ? <li>부서 {deptHint}</li> : null}
            {userHint ? <li>담당 {userHint}</li> : null}
          </HintList>
        </Panel>
        <Panel>
          <strong style={{ fontSize: 13 }}>ACL 목록</strong>
          <Table>
            <thead>
              <tr>
                <th>aclId</th>
                <th>formId</th>
                <th>허용 부서</th>
                <th>허용 담당</th>
                <th>비고</th>
                <th>사용</th>
              </tr>
            </thead>
            <tbody>
              {acls.length === 0 ? (
                <tr>
                  <td colSpan={6}>ACL 없음 · 전원 허용(기본)</td>
                </tr>
              ) : (
                acls.map((a) => (
                  <tr
                    key={a.aclId}
                    className={`${selectedId === a.aclId ? 'active' : ''} ${a.useYn === 'N' ? 'disabled' : ''}`}
                    onClick={() => selectAcl(a)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{a.aclId}</td>
                    <td>{a.formId}</td>
                    <td>{(a.allowDeptCodes || []).join(', ') || '—'}</td>
                    <td>{(a.allowUserIds || []).join(', ') || '—'}</td>
                    <td>{a.remark || ''}</td>
                    <td>{a.useYn || 'Y'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Panel>
        <Panel>
          <strong style={{ fontSize: 13 }}>ACL 편집</strong>
          <FormArea dense fields={editFields} />
        </Panel>
        <Panel>
          <strong style={{ fontSize: 13 }}>권한 시험</strong>
          <FormArea dense fields={testFields} />
          <Meta>선택한 양식에 대해 canUseForm을 즉시 확인합니다.</Meta>
        </Panel>
      </FormProvider>
    </Stack>
  );
}
